import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { TNode, TreeApi, TreeData } from '@revelio/ui';

import {
  forgetPicks,
  recordPicks,
  type SelectionSourceMap,
} from '../../../../../next/CustomDash/filters/utils/selection-source';
import { fetchCompanyByRcid } from '../../../../api/fetchCompanyByRcid';
import { SelectionListControls } from '../../../../selection-lists/components/SelectionListControls/SelectionListControls';
import {
  CompanySelectionProvider,
  getSelectionLeafId,
} from '../../company-selection-context';
import { EntityInfoOpenProvider } from '../../entity-info-open-context';
import type { ChipSourceKey, TooltipAddSource } from '../../shortcut-source';
import { useTreeSelections } from '../../use-tree-selections';
import type { PopoutTreeProps } from '../PopoutTree';
import treeStyles from '../PopoutTree.module.css';
import type { RecentEntity } from '../RecentEntitiesPanel';
import type { BasePopoutTreeCommonProps } from '../types';
import { AsyncCompanySearchTree } from './AsyncCompanySearchTree';
import styles from './all-companies-popout-tree.module.css';
import { SelectedValuesDisplayComponent } from './SelectedValueDisplay';

/** Returns whether the toggle was applied — an add can be refused by a cap. */
export type ShortlistToggle = (
  entity: RecentEntity,
  isSelected: boolean,
  source: ChipSourceKey
) => boolean;

export type ShortlistRenderer = (api: {
  selectedIds: string[];
  onToggle: ShortlistToggle;
}) => React.ReactNode;

export interface AllCompaniesPopoutTreeProps extends BasePopoutTreeCommonProps {
  treeData: TreeData[];
  activeIds: string[];
  activeNodeData?: TreeData[];
  maxTopLevelSelections?: number;
  searchInputRef?: PopoutTreeProps['searchInputRef'];
  onSubmit: (
    selectedItems: string[],
    selectedNodeData: TNode[],
    /** Where each pending id was picked from, keyed by id. */
    pickSources: SelectionSourceMap
  ) => void;
  /** Told the pending selection's size just before "Clear selections" empties it. */
  onClearSelections?: (pendingSelectionCount: number) => void;
  /** A company added from the info tooltip's parent or competitor control. */
  onTooltipCompanyAdded?: (source: TooltipAddSource) => void;
  middleContent?: React.ReactNode;
  bottomContent?: React.ReactNode;
  showSelectedValues?: boolean;
  replaceSingleSelections?: boolean;
  /**
   * The shortcut entities offered above the tree (recents, competitors, a
   * trial plan's companies). Rendered by the caller because which shortlist
   * applies is its decision, but the pending selection it drives lives here.
   */
  renderShortlist?: ShortlistRenderer;
  /**
   * Whether a pending tree id stands for this shortlist entity. Defaults to an
   * exact match; the caller overrides it where the two carry the same entity
   * under different ids (a bare entity id against a tree path).
   */
  matchesShortlistEntity?: (pendingId: string, entity: RecentEntity) => boolean;
  /**
   * Whether the pending selection may be submitted. Evaluated here because the
   * pending selection is this component's state; the caller owns the rule.
   */
  isSelectionSubmittable?: (selections: string[]) => boolean;
  /** Tooltip shown when `isSelectionSubmittable` rejects the selection. */
  submitDisabledTooltip?: string;
  /**
   * Select companies only: a flat headcount-ordered list instead of the
   * industry drill tree, and no Industry tab in the search results.
   */
  companyOnly?: boolean;
}

export function AllCompaniesPopoutTree({
  treeData,
  activeIds,
  activeNodeData = [],
  filterLabel,
  minSelections = 0,
  maxSelections,
  maxTopLevelSelections,
  branches,
  disabledLevels,
  disallowMultiLevelSelect = false,
  sortSuggestedSearch = false,
  isOpen,
  searchInputRef,
  onSubmit,
  onClearSelections,
  onTooltipCompanyAdded,
  onClose,
  middleContent,
  bottomContent,
  showSelectedValues = false,
  replaceSingleSelections = false,
  renderShortlist,
  matchesShortlistEntity = (pendingId, entity) => pendingId === entity.id,
  isSelectionSubmittable,
  submitDisabledTooltip,
  companyOnly = false,
}: AllCompaniesPopoutTreeProps) {
  const {
    selections,
    setSelections,
    onChange,
    onClear,
    validateMinSelections,
    selectedNodeData,
  } = useTreeSelections({
    initialSelections: activeIds,
    disallowMultiLevelSelect,
    maxSelections,
    minSelections,
    filterLabel,
    maxTopLevelSelections,
    replaceSingleSelections,
  });

  useEffect(() => {
    setSelections(activeIds);
  }, [activeIds, setSelections]);

  const resetSelections = useCallback(() => {
    setSelections(activeIds);
  }, [activeIds, setSelections]);

  const popoutTreeRef = useRef<
    TreeApi & { onResetSearch: () => void; scrollToTop: () => void }
  >();
  // Where each pending pick came from, handed back with the submit. Starts
  // over each time the picker opens so one session never inherits another's.
  const pickSourcesRef = useRef<SelectionSourceMap>(new Map());

  useEffect(() => {
    if (isOpen) {
      pickSourcesRef.current.clear();
    }
    const treeApi = popoutTreeRef.current;
    if (!treeApi) {
      // Tree component might not be mounted yet
      return;
    }

    if (isOpen) {
      resetSelections(); // Reset selections based on activeIds
      treeApi.onSearch(''); // Trigger initial/empty search

      treeApi.scrollToTop?.();
      return;
    }

    treeApi.onResetSearch?.(); // Reset search state (clear input, cancel requests)

    // Dependencies: Run when isOpen changes or when resetSelections identity changes.
  }, [isOpen, resetSelections]);

  // Company-selection add-context for the tooltip "+" (subsidiary parent +
  // competitors). Adding fetches the company's RICS ancestry by rcid so it
  // commits as a path-encoded company id, matching a normal tree pick. The
  // in-flight commit reads a ref so a selection made during the ~100ms lookup
  // isn't dropped by the stale snapshot.
  const selectionsRef = useRef(selections);
  selectionsRef.current = selections;
  const [pendingRcid, setPendingRcid] = useState<number | null>(null);

  // Which row's info tooltip is open, remembered across the tree's row remounts
  // so toggling a company from inside the tooltip doesn't close it. Held in a
  // ref (writing it never re-renders) by this non-remounting container.
  const entityInfoOpenIdRef = useRef<string | null>(null);
  const entityInfoExpandedRcidRef = useRef<number | null>(null);
  const entityInfoReportedParentRcidRef = useRef<number | null>(null);
  const entityInfoOpenStore = useMemo(
    () => ({
      openIdRef: entityInfoOpenIdRef,
      expandedRcidRef: entityInfoExpandedRcidRef,
      reportedParentRcidRef: entityInfoReportedParentRcidRef,
    }),
    []
  );

  const isCompanySelected = useCallback(
    (rcid: number) =>
      selections.some((id) => getSelectionLeafId(id) === String(rcid)),
    [selections]
  );

  const toggleCompany = useCallback(
    async (
      { rcid, name }: { rcid: number; name: string },
      source: TooltipAddSource
    ) => {
      const target = String(rcid);
      if (
        selectionsRef.current.some((id) => getSelectionLeafId(id) === target)
      ) {
        const kept = selectionsRef.current.filter(
          (id) => getSelectionLeafId(id) !== target
        );
        forgetPicks(pickSourcesRef.current, kept);
        setSelections(kept);
        return;
      }
      setPendingRcid(rcid);
      try {
        const company = await fetchCompanyByRcid(rcid);
        const ids = company?.primary_identifiers;
        const pathId =
          ids && ids.rics_k10 != null && ids.rics_k400 != null
            ? `${ids.rics_k10}_${ids.rics_k400}_${rcid}`
            : target;
        const node = {
          id: pathId,
          data: { id: pathId, name },
        } as unknown as TNode;
        const next = [...selectionsRef.current, pathId];
        recordPicks(pickSourcesRef.current, next, 'shortlist');
        if (onChange(next, [node])) onTooltipCompanyAdded?.(source);
      } catch {
        // Leave unselected on lookup failure — a safe no-op.
      } finally {
        setPendingRcid(null);
      }
    },
    [onChange, setSelections, onTooltipCompanyAdded]
  );

  const companySelectionValue = useMemo(
    () => ({ isCompanySelected, toggleCompany, pendingRcid }),
    [isCompanySelected, toggleCompany, pendingRcid]
  );

  const handleSubmit = () => {
    if (!validateMinSelections()) {
      return;
    }

    onSubmit(selections, selectedNodeData, pickSourcesRef.current);
    pickSourcesRef.current.clear();

    if (onClose) {
      onClose();
    }
  };

  const activeNodeDataIdSet = new Set(activeNodeData.map((node) => node.id));
  const selectedNodeTreeData = selectedNodeData.map((node) => node.data);
  const filteredSelectedNodeTreeData = selectedNodeTreeData.filter(
    (node) => !activeNodeDataIdSet.has(node.id)
  );

  const allNodeData = [...activeNodeData, ...filteredSelectedNodeTreeData];

  // Get selected TreeData for display
  const selectedTreeData = allNodeData?.filter((node) =>
    selections.includes(node.id)
  );

  // Handle selection removal from SelectedValuesDisplay
  const handleRemoveSelection = (selectionId: string) => {
    const updatedSelections = selections.filter((id) => id !== selectionId);
    forgetPicks(pickSourcesRef.current, updatedSelections);
    setSelections(updatedSelections);
  };

  if (!treeData || treeData.length === 0) {
    return null;
  }

  const shortlistContent = renderShortlist?.({
    selectedIds: selections,
    onToggle: (entity, isSelected) => {
      if (!isSelected) {
        const kept = selections.filter(
          (id) => !matchesShortlistEntity(id, entity)
        );
        forgetPicks(pickSourcesRef.current, kept);
        setSelections(kept);
        return true;
      }
      const next = [...selections, entity.id];
      recordPicks(pickSourcesRef.current, next, 'shortlist');
      return onChange(next);
    },
  });

  // The search text at the moment of the pick is what separates a searched
  // company from a browsed one; the tree's imperative handle carries it live.
  const handleTreeChange = (value: string[], nodes?: TNode[]) => {
    recordPicks(
      pickSourcesRef.current,
      value,
      popoutTreeRef.current?.search ? 'search' : 'browse'
    );
    onChange(value, nodes);
  };

  const handleClear = () => {
    onClearSelections?.(selections.length);
    pickSourcesRef.current.clear();
    onClear();
  };

  const isSubmittable = isSelectionSubmittable?.(selections) ?? true;

  const content = (
    <div
      className={classNames(
        treeStyles.formStack,
        styles.allCompaniesPopoutTreeContainer
      )}
      data-testid="all-companies-popout-tree-container"
    >
      {showSelectedValues && selectedTreeData.length > 0 && (
        <SelectedValuesDisplayComponent
          selections={selectedTreeData}
          onRemove={handleRemoveSelection}
        />
      )}
      <div className={styles.allCompaniesPopoutTreeContainer}>
        <AsyncCompanySearchTree
          data={treeData}
          value={selections}
          sortSelectedIds={activeIds}
          disabledLevels={disabledLevels}
          onChange={handleTreeChange}
          branches={branches}
          sortSuggestedSearch={sortSuggestedSearch}
          searchInputRef={searchInputRef}
          ref={popoutTreeRef}
          selectedNodeData={selectedNodeData}
          isOpen={isOpen}
          middleContent={middleContent}
          recentContent={shortlistContent}
          companyOnly={companyOnly}
        />
      </div>
      {bottomContent && <div>{bottomContent}</div>}
      <SelectionListControls
        onClear={handleClear}
        onClose={onClose}
        onSubmit={handleSubmit}
        isSubmitDisabled={selections.length === 0 || !isSubmittable}
        submitText={activeIds.length ? 'Update' : 'Add'}
        tooltipText={isSubmittable ? undefined : submitDisabledTooltip}
      />
    </div>
  );

  return (
    <EntityInfoOpenProvider value={entityInfoOpenStore}>
      <CompanySelectionProvider value={companySelectionValue}>
        {content}
      </CompanySelectionProvider>
    </EntityInfoOpenProvider>
  );
}
